import React from 'react';
import { Button, Card, OverlayTrigger, Popover } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  BloodworkAnalysis,
  CatalogSupplement,
  FurtherConsiderations,
  Section,
  SupplementEdit,
  addedSupplementEdits,
  applySupplementOverride,
  indexSupplementEdits,
  isMeaningfulSupplementEdit,
  renameSupplementEdit,
  resetSupplementEdit,
  withSupplementEdit,
  withoutSupplementEdit,
} from '../../models';
import { Bloodwork } from '../../models/Bloodwork';
import {
  doseForProduct,
  normalizeSupplementName,
} from '../../models/holobiomeKits';
import {
  CELL,
  HEAD_CELL,
  ClientListSelection,
  ProductRow,
  collectTopPriorities,
  renderSupplementName,
  scopedGroups,
} from './supplements-summary-panel';
import {
  SupplementsBlock,
  packSupplementBlocks,
} from '../../utils/pdfSupplementsPagination';
import { SupplementPicker } from './supplement-picker';

// ---------------------------------------------------------------------------
// Recommended Supplements — the practitioner's curated list, for the client.
//
// The by-system tables answer "what did this blood work point to, and why",
// which is a practitioner's question: they carry pattern numbers, the system
// that recommended each product, and the month it starts. This card answers the
// client's question instead — what am I taking, what is it for, how much — and
// carries no pattern, no system and no diagnosis.
//
// It is its own card rather than a block inside the Holobiome section, because
// the Holobiome section is gated on the Systemic Formulas checkbox: a client
// list living inside it would vanish when that brand is unchecked, and could
// never hold a product from another brand. Today every tickable product is
// Systemic Formulas; when a second brand's table grows the same Client List
// column, this card is already where those products would land.
// ---------------------------------------------------------------------------

// What one line of the client's list needs. `dose` is resolved here rather than
// stored, so a catalog that learns about forms later (see doseForProduct)
// improves every existing report rather than only new ones.
export type ClientListProduct = {
  name: string;
  description: string;
  link?: string;
  dose: string;
};

// STORAGE IS INVERTED: the note holds the products the practitioner UNTICKED,
// not the ones they ticked. A list of inclusions could not express "no note" —
// an absent note would mean an empty client list, so every report that had
// never been curated would start blank rather than start somewhere.
//
// One normalized product name per line, so the note stays diffable and a name
// with a comma or a µ in it can't break the parse.
const parseExcluded = (text: string | undefined): Set<string> =>
  new Set(
    (text ?? '')
      .split('\n')
      .map(line => normalizeSupplementName(line))
      .filter(Boolean),
  );

// WHERE AN UNCURATED LIST STARTS: the Top Priority shortlist, ticked, and
// nothing else.
//
// It used to start with everything this test recommends ticked, which on a busy
// report is twelve or more products — a client list nobody would hand over as
// it stands, so the practitioner's first job was always to untick most of it.
// The shortlist is the answer to "what first", so it is the better place to
// start from: the practitioner adds to it rather than cutting it down.
//
// Everything else is EXCLUDED rather than absent, so it stays visible and
// untickable in the By System column — the shortlist is a starting point, not
// a filter on what the practitioner may choose.
export function defaultExcludedFromClientList(
  bloodworkAnalysis: BloodworkAnalysis | undefined,
  furtherConsiderations: FurtherConsiderations | undefined,
  enabledSupplementBrands: string[] | undefined,
): Set<string> {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) {
    return new Set<string>();
  }
  const shortlisted = new Set(
    collectTopPriorities(
      bloodworkAnalysis,
      furtherConsiderations,
      enabledSupplementBrands,
    ).map(product => normalizeSupplementName(product.name)),
  );
  const excluded = new Set<string>();
  scopedGroups(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
    'systemicFormulas',
  ).forEach(({ sections }) =>
    sections.forEach(({ products }) =>
      products.forEach(product => {
        const key = normalizeSupplementName(product.name);
        if (!shortlisted.has(key)) excluded.add(key);
      }),
    ),
  );
  return excluded;
}

// The note when there is one, the default above when there is not.
//
// A note that EXISTS wins even when it is empty: an empty exclusion note is the
// practitioner having ticked everything back on, which is a decision, and
// falling back to the default there would silently undo it. So the test is
// "does the note exist", not "does it have anything in it".
//
// The two model arguments are optional only so a caller with no considerations
// to hand still reads a stored note correctly; without them there is nothing to
// derive a default FROM, and an uncurated list comes back empty (everything
// ticked), which is where this started.
export function readExcludedFromClientList(
  bloodworkAnalysis: BloodworkAnalysis | undefined,
  furtherConsiderations?: FurtherConsiderations,
  enabledSupplementBrands?: string[],
): Set<string> {
  const note = bloodworkAnalysis?.bloodwork?.bloodworkNotes?.findNote(
    Section.RECOMMENDED_SUPPLEMENTS,
  );
  if (note) return parseExcluded(note.text);
  return defaultExcludedFromClientList(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  );
}

// Writes the exclusion note in place. The caller saves the bloodwork — this
// only decides what the note says, so a test can assert the round trip without
// a store.
export function writeExcludedFromClientList(
  bloodwork: Bloodwork,
  excluded: Set<string>,
): void {
  const notes = bloodwork?.bloodworkNotes;
  if (!notes) return;
  // Sorted so two sessions that untick the same two products produce the same
  // note, rather than a diff that depends on click order.
  const text = Array.from(excluded).sort().join('\n');
  const note = notes.findNote(Section.RECOMMENDED_SUPPLEMENTS);
  if (note) note.text = text;
  else notes.addNote(Section.RECOMMENDED_SUPPLEMENTS, text);
}

// ─────────────────── the practitioner's own edits ───────────────────
//
// Stored on the test itself (Bloodwork.supplementEdits) rather than in a note:
// a note is a flat list of names, one per line, which is what keeps the
// exclusion note above diffable, and these carry per-row text that can contain
// anything the practitioner types. See SupplementEdit.ts for why an edit stores
// an override rather than a copy of the catalog row.

export function readSupplementEdits(
  bloodworkAnalysis: BloodworkAnalysis | undefined,
): SupplementEdit[] {
  const edits = bloodworkAnalysis?.bloodwork?.supplementEdits;
  return Array.isArray(edits) ? edits.map(edit => new SupplementEdit(edit)) : [];
}

// Writes the edits in place. The caller saves the bloodwork — this only decides
// what the record says, so a test can assert the round trip without a store.
//
// Empty is stored as `undefined`, never `[]`: DynamoDB keeps the attribute
// either way, and an empty array comes back through the entry form's Number()
// rebuild as 0 (see the guard in Bloodwork's constructor).
export function writeSupplementEdits(
  bloodwork: Bloodwork,
  edits: SupplementEdit[],
): void {
  if (!bloodwork) return;
  const meaningful = (edits || []).filter(isMeaningfulSupplementEdit);
  bloodwork.supplementEdits = meaningful.length ? meaningful : undefined;
}

// The catalog row an added product came from, if the catalog has one. By tag
// where the picker recorded one, by name otherwise — a product typed by hand
// carries no tag and usually matches nothing, which is the "practitioner's own
// entry" case the card exists to hold.
function catalogRowFor(
  edit: SupplementEdit,
  catalog: Map<string, CatalogSupplement>,
): CatalogSupplement | undefined {
  return (
    (edit.tag ? catalog.get(`tag:${edit.tag}`) : undefined) ||
    catalog.get(`name:${normalizeSupplementName(edit.name)}`)
  );
}

// Is this added row a product of a brand the practitioner has switched OFF in
// Supplement Settings?
//
// Unchecking a brand takes its products off every other part of the report —
// the whole Systemic Formulas section goes, and findConsiderations stops
// returning its rows — but an added row is stored on the test rather than
// derived from the considerations, so it used to survive the brand going away
// and leave the client's list as the one place a switched-off brand still sold
// something.
//
// FILTERED AT RENDER, NEVER DELETED: the edit stays on the record, so checking
// the brand back on puts the product back on the list rather than quietly
// costing the practitioner their curation.
//
// A product the catalog has never heard of belongs to no brand and is never
// dropped. That is exactly what this card keeps offering when no brand is
// switched on at all.
function isFromSwitchedOffBrand(
  edit: SupplementEdit,
  catalog: Map<string, CatalogSupplement>,
  enabledSupplementBrands: string[] | undefined,
): boolean {
  if (!enabledSupplementBrands) return false;
  const brand = catalogRowFor(edit, catalog)?.brand;
  return !!brand && !enabledSupplementBrands.includes(brand);
}

// A product the practitioner added, resolved for display.
//
// The catalog is read at RENDER time, by tag where there is one and by name
// otherwise, so a description improved in the catalog improves this row too —
// the same thing that is already true of the rows the analysis puts on the
// card. What the practitioner typed always wins over both.
function addedProduct(
  edit: SupplementEdit,
  catalog: Map<string, CatalogSupplement>,
): ClientListProduct {
  const row = catalogRowFor(edit, catalog);
  return {
    name: row ? row.name : edit.name,
    description: applySupplementOverride(edit.description, row?.description || ''),
    // No slug-guessing here. systemicFormulasProductUrl only knows one brand's
    // URLs, and a made-up link on a product from anywhere else points at a page
    // that doesn't sell it. No link means the name renders bold and unlinked.
    link: (edit.link || '').trim() || row?.link,
    // NO DOSE GUESS FOR A PRODUCT THE CATALOG HAS NEVER HEARD OF, for the same
    // reason there is no link guess. doseForProduct falls back to the Systemic
    // Formulas capsule protocol for any name it doesn't recognize, which was
    // right when every row on this card was theirs; on a product typed in by
    // hand it prints a serving nobody stated, for something that may not even
    // be a capsule, on a page the client is meant to act on. Typed or blank.
    dose: applySupplementOverride(edit.dose, row ? doseForProduct(row.name) : ''),
  };
}

// Catalog indexed for the two lookups an edit needs.
//
// UNFILTERED BY BRAND, deliberately: this index answers "what does the catalog
// know about this product", which includes what brand it belongs to, and a
// brand-filtered catalog cannot answer that for a product whose brand is off.
// isFromSwitchedOffBrand does the filtering, and a row it drops is never
// resolved for display.
function catalogIndex(
  furtherConsiderations: FurtherConsiderations | undefined,
): Map<string, CatalogSupplement> {
  const index = new Map<string, CatalogSupplement>();
  (furtherConsiderations?.supplementCatalog() || []).forEach(row => {
    if (row.tag) index.set(`tag:${row.tag}`, row);
    index.set(`name:${normalizeSupplementName(row.name)}`, row);
  });
  return index;
}

// Every product the by-system tables offer a checkbox for, deduped by name.
//
// Read off scopedGroups — the same collection the tables render — so the list
// can only ever contain products the practitioner actually saw and could have
// unticked. A product several systems recommend has one line here, however many
// rows it has up there.
//
// ORDER: the Top Priority shortlist first, in its own rank order, then
// everything else system by system, worst system first, and within a system the
// order that system lists them in.
//
// The shortlist leads because it is the answer to the question the client is
// actually asking — what do I start with — and because it is what the report
// two panels up has just told them. A client list that ordered those seven
// among everything else would be the same page disagreeing with itself about
// what comes first. scopedGroups already returns the rest in severity order
// (rankSectionsBySeverity), so this only has to lift the shortlist out and not
// destroy what's left: a Map keeps insertion order, and a product claimed by
// two systems is kept where the WORSE one put it.
//
// It sorted by month before, which read as a schedule the card doesn't
// otherwise carry and buried the most concerning system's products among
// everything else that happened to start in Month 1. The list is a list of
// priorities, not a calendar; the roadmap is where the calendar lives.
//
// The practitioner's own edits land on top, in two ways: an 'added' edit puts a
// product on the end of the list, and any edit overrides the text of the row it
// names — whether the analysis put that row there or the practitioner did. One
// resolution path for both, so an identified row and an added row for the same
// product can never print differently.
export function collectClientListProducts(
  bloodworkAnalysis: BloodworkAnalysis | undefined,
  furtherConsiderations: FurtherConsiderations | undefined,
  enabledSupplementBrands: string[] | undefined,
  excluded: Set<string>,
  edits?: SupplementEdit[],
): ClientListProduct[] {
  if (!bloodworkAnalysis?.bloodwork || !furtherConsiderations) return [];
  const groups = scopedGroups(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
    'systemicFormulas',
  );
  const byName = new Map<string, ProductRow>();
  // Seeded with the shortlist, in rank order, so those keys claim the front of
  // the Map before the severity walk below adds anything. Nothing is looked up
  // from the shortlist itself — the ProductRow the tables render is still what
  // gets stored, so a shortlisted product's description, link and dose are
  // resolved exactly as they would be if it had arrived through the walk.
  const shortlist = collectTopPriorities(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
  ).map(product => normalizeSupplementName(product.name));
  const rank = new Map(shortlist.map((key, i) => [key, i] as const));
  groups.forEach(({ sections }) =>
    sections.forEach(({ products }) =>
      products.forEach(product => {
        const key = normalizeSupplementName(product.name);
        if (!byName.has(key)) byName.set(key, product);
      }),
    ),
  );
  // Lift the shortlist to the front. A stable sort on "rank, or after every
  // rank" keeps the severity order of everything else exactly as the walk built
  // it — Array.prototype.sort is stable in every engine this ships to.
  const ordered = Array.from(byName.entries()).sort(
    ([a], [b]) =>
      (rank.get(a) ?? shortlist.length) - (rank.get(b) ?? shortlist.length),
  );

  const editsByName = indexSupplementEdits(edits);
  const identified: ClientListProduct[] = ordered
    .filter(([key]) => !excluded.has(key))
    .map(([key, product]) => {
      const edit = editsByName.get(key);
      return {
        name: product.name,
        description: applySupplementOverride(edit?.description, product.description),
        link: (edit?.link || '').trim() || product.link,
        dose: applySupplementOverride(edit?.dose, doseForProduct(product.name)),
      };
    });

  // Added products follow the identified ones, in the order they were added.
  // A product the analysis already listed is NOT repeated here: the picker
  // shows those as already on the list, and adding one back after it was
  // unticked drops it from `excluded` rather than making a second row for the
  // same bottle (see toggleClientListProduct in bloodwork-test-analysis).
  const addedEdits = addedSupplementEdits(edits).filter(
    edit => !byName.has(normalizeSupplementName(edit.name)),
  );
  if (addedEdits.length === 0) return identified;

  // Built only when there is something to resolve. Walking the whole catalog on
  // every render of a card that usually has no added products is the kind of
  // cost this file already went out of its way to avoid.
  const catalog = catalogIndex(furtherConsiderations);
  return [
    ...identified,
    ...addedEdits
      .filter(
        edit => !isFromSwitchedOffBrand(edit, catalog, enabledSupplementBrands),
      )
      .map(edit => addedProduct(edit, catalog)),
  ];
}

// What each row would say with the practitioner's edits taken off.
//
// Edit mode needs it twice over: to fill an input with the text being edited,
// and to tell an edited row from an untouched one (which is what puts Reset on
// a row and what stops a "changed" description that merely retypes the catalog
// from being stored).
//
// Only built in edit mode. It walks the considerations a second time, and that
// walk is the reason this card collects once and filters rather than collecting
// twice per render — but edit mode is a deliberate, short-lived state, and the
// alternative (threading a second value through ClientListProduct into the PDF
// types, which never need it) puts the cost in the wrong place.
export function clientListBaseTexts(
  bloodworkAnalysis: BloodworkAnalysis | undefined,
  furtherConsiderations: FurtherConsiderations | undefined,
  enabledSupplementBrands: string[] | undefined,
  excluded: Set<string>,
  edits: SupplementEdit[],
): Map<string, { description: string; dose: string; link?: string }> {
  const base = new Map<string, { description: string; dose: string; link?: string }>();
  collectClientListProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
    excluded,
    [],
  ).forEach(product =>
    base.set(normalizeSupplementName(product.name), {
      description: product.description,
      dose: product.dose,
      link: product.link,
    }),
  );

  // An added row has no analysis behind it, so its unedited form is whatever
  // the catalog says — or nothing at all, for a product typed by hand.
  const addedEdits = addedSupplementEdits(edits);
  if (addedEdits.length === 0) return base;
  const catalog = catalogIndex(furtherConsiderations);
  addedEdits.forEach(edit => {
    const key = normalizeSupplementName(edit.name);
    if (base.has(key)) return;
    const row = catalogRowFor(edit, catalog);
    base.set(key, {
      description: row?.description || '',
      // Same rule as addedProduct: a hand-typed product has no catalog serving
      // behind it, so Reset empties the box rather than filling it with a
      // capsule protocol the practitioner never chose.
      dose: row ? doseForProduct(row.name) : '',
      link: row?.link,
    });
  });
  return base;
}

// The selection object the tables take. Built here so the inversion above stays
// in this file: the tables ask "is this on the list", not "is this excluded".
export function clientListSelection(
  excluded: Set<string>,
  onToggle: (productName: string) => void,
): ClientListSelection {
  return {
    isSelected: (productName: string) =>
      !excluded.has(normalizeSupplementName(productName)),
    toggle: onToggle,
  };
}

const COLGROUP = (
  <colgroup>
    <col style={{ width: '18%' }} />
    <col style={{ width: '55%' }} />
    <col style={{ width: '27%' }} />
  </colgroup>
);

const TABLE_STYLE: React.CSSProperties = {
  width: '100%',
  tableLayout: 'fixed',
  borderCollapse: 'collapse',
  fontSize: '12px',
};

const HEADER_CELLS = (
  <>
    <th style={HEAD_CELL}>Product</th>
    <th style={HEAD_CELL}>Description</th>
    <th style={HEAD_CELL}>Dosage</th>
  </>
);

// The lead-in. Client-facing: no "you", no pattern, no system, and no claim
// that the list is exhaustive — the practitioner decides what is on it.
const LEAD_IN =
  'The supplements recommended from this test, with the standard protocol serving for each. Adjust per practitioner guidance.';

function productLine(product: ClientListProduct, isLast: boolean): JSX.Element {
  const cell = isLast ? { ...CELL, borderBottom: 'none' } : CELL;
  return (
    <tr key={product.name}>
      <td style={cell}>{renderSupplementName(product)}</td>
      <td style={cell}>{product.description}</td>
      <td style={cell}>{product.dose}</td>
    </tr>
  );
}

// ───────────────────────── edit mode ─────────────────────────
//
// PRACTITIONER-ONLY, and only on screen. What prints is the result of these
// decisions, with no mark of any kind separating a product the analysis found
// from one the practitioner added: the client is reading a protocol, not an
// audit trail.

// Wider in the Product column than the read-only table, because that cell
// holds the name AND the link box for a row the practitioner added.
const EDIT_COLGROUP = (
  <colgroup>
    <col style={{ width: '24%' }} />
    <col style={{ width: '40%' }} />
    <col style={{ width: '22%' }} />
    <col style={{ width: '14%' }} />
  </colgroup>
);

const EDIT_INPUT: React.CSSProperties = {
  width: '100%',
  fontSize: '12px',
  padding: '2px 6px',
  border: '1px solid #ced4da',
  borderRadius: '3px',
  lineHeight: 1.4,
};

// A description box that grows to fit what is in it.
//
// A fixed height clipped the longer catalog descriptions behind a scrollbar
// inside a table cell — the practitioner could not read the text they were
// being asked to edit. Same fitToContent trick the Notes editor uses, applied
// on mount and on every keystroke.
const GrowingTextarea: React.FC<{
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}> = ({ value, placeholder, onChange }) => {
  const ref = React.useRef<HTMLTextAreaElement | null>(null);
  const fit = () => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.max(el.scrollHeight, 46)}px`;
  };
  // Re-fit when the text changes underneath us as well as when it is typed —
  // Reset puts the catalog wording back, which is usually a different length.
  React.useEffect(fit, [value]);
  return (
    <textarea
      ref={ref}
      style={{ ...EDIT_INPUT, overflow: 'hidden', resize: 'none' }}
      value={value}
      placeholder={placeholder}
      onChange={e => {
        onChange(e.target.value);
        fit();
      }}
    />
  );
};

// The product name, for a row the practitioner typed in themselves.
//
// COMMITS ON BLUR, not on every keystroke: the name is the key every edit and
// every tick is stored under, so a rename rebuilds the row — including its
// React key, which remounts the input and would drop the cursor mid-word. Local
// state holds what is being typed until the practitioner leaves the box.
//
// It resets to the committed name when that name changes underneath it, which
// is what puts the old text back if the rename was refused (a blank name, or a
// product already on the list).
const NameInput: React.FC<{
  name: string;
  onCommit: (value: string) => void;
}> = ({ name, onCommit }) => {
  const [text, setText] = React.useState(name);
  React.useEffect(() => setText(name), [name]);
  return (
    <input
      type="text"
      style={EDIT_INPUT}
      value={text}
      placeholder="Product name"
      title="The product name, as the client will read it"
      onChange={e => setText(e.target.value)}
      onBlur={() => onCommit(text)}
      onKeyDown={e => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        if (e.key === 'Escape') setText(name);
      }}
    />
  );
};

// A link is optional, and only ever the practitioner's: nothing guesses one
// (systemicFormulasProductUrl knows one brand's URLs, and a made-up link on
// another brand's product points at a page that doesn't sell it). A row added
// without one had no way to gain one afterwards, which is what this box is for.
//
// COMMITS ON BLUR, like the name box, and only when what is in it is a link.
// An href of "systemicformulas" resolves against the app's own origin, so a
// half-typed URL would print as a link to a LabSmarts page that doesn't exist.
// The same rule the manual-entry form applies at the point of adding, applied
// again to the same field afterwards.
const LinkInput: React.FC<{
  link: string;
  onCommit: (value: string) => void;
}> = ({ link, onCommit }) => {
  const [text, setText] = React.useState(link);
  React.useEffect(() => setText(link), [link]);
  const trimmed = text.trim();
  const isBad = !!trimmed && !/^https?:\/\//i.test(trimmed);
  return (
    <>
      <input
        type="text"
        style={{ ...EDIT_INPUT, marginTop: '4px' }}
        value={text}
        placeholder="Product link (optional)"
        title="Where the client buys it. Leave empty to print the name unlinked."
        onChange={e => setText(e.target.value)}
        onBlur={() => !isBad && onCommit(trimmed)}
        onKeyDown={e => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
          if (e.key === 'Escape') setText(link);
        }}
      />
      {isBad && (
        <div style={{ fontSize: '10.5px', color: '#c0392b', marginTop: '2px' }}>
          A link has to start with http:// or https://
        </div>
      )}
    </>
  );
};

export type ClientListRowEdit = {
  product: ClientListProduct;
  /** The same row with the practitioner's edits taken off. */
  base: { description: string; dose: string; link?: string };
  isAdded: boolean;
  isEdited: boolean;
  /**
   * The practitioner's OWN product: added by hand, and nothing in the catalog
   * answers to its tag or its name. Its name and its link are theirs to change.
   *
   * A catalog product is the opposite on both counts. Its name is the catalog's
   * (addedProduct resolves the name from the row, so a rename stored here would
   * be a name the card then ignores), and its link is the Systemic Formulas URL
   * held in the sheet: a box over it is only ever a way to break a working
   * product link, on a page the practitioner does not own.
   */
  isOwnProduct: boolean;
};

function EditableClientList({
  rows,
  onFieldChange,
  onRename,
  onReset,
  onRemove,
}: {
  rows: ClientListRowEdit[];
  onFieldChange: (
    name: string,
    field: 'description' | 'dose' | 'link',
    value: string,
  ) => void;
  onRename: (name: string, value: string) => void;
  onReset: (name: string) => void;
  onRemove: (name: string) => void;
}): JSX.Element {
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={TABLE_STYLE}>
        {EDIT_COLGROUP}
        <thead>
          <tr>
            <th style={HEAD_CELL}>Product</th>
            <th style={HEAD_CELL}>Description</th>
            <th style={HEAD_CELL}>Dosage</th>
            <th style={HEAD_CELL}> </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ product, base, isAdded, isEdited, isOwnProduct }) => {
            return (
              <tr key={product.name}>
                <td style={CELL}>
                  {isOwnProduct ? (
                    <NameInput
                      name={product.name}
                      onCommit={value => onRename(product.name, value)}
                    />
                  ) : (
                    renderSupplementName(product)
                  )}
                  {/* THE PRACTITIONER'S OWN PRODUCTS ONLY. Every other row on
                      this card is a catalog product whose link is held in the
                      sheet and is already right; a box over it would only ever
                      be a way to break one. */}
                  {isOwnProduct && (
                    <LinkInput
                      link={product.link || ''}
                      onCommit={value => onFieldChange(product.name, 'link', value)}
                    />
                  )}
                </td>
                <td style={CELL}>
                  <GrowingTextarea
                    value={product.description}
                    placeholder="What it is for (printed for the client)"
                    onChange={value =>
                      onFieldChange(product.name, 'description', value)
                    }
                  />
                </td>
                <td style={CELL}>
                  <input
                    type="text"
                    style={EDIT_INPUT}
                    value={product.dose}
                    placeholder="Dosage"
                    onChange={e => onFieldChange(product.name, 'dose', e.target.value)}
                  />
                </td>
                <td style={{ ...CELL, textAlign: 'center', whiteSpace: 'nowrap' }}>
                  {isEdited && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      style={{ padding: '0 4px', fontSize: '11px' }}
                      // "The catalog wording" is not what Reset restores on a
                      // row the catalog has never heard of: there it empties
                      // the boxes, and saying "catalog" would read as a promise
                      // that something is coming back.
                      title={
                        base.description || base.dose || base.link
                          ? 'Put this row back to the catalog wording. The product stays on the list.'
                          : 'Clear what was typed on this row. The product stays on the list.'
                      }
                      onClick={() => onReset(product.name)}
                    >
                      Reset
                    </button>
                  )}
                  {/* Only an added row can be removed. Taking an identified
                      product off the list is what its Client List tick in the
                      By System table already does, and two controls that both
                      mean "off" would be two states to reconcile.

                      Styled as the app's row-delete, the same one the
                      Additional Markers table uses: a trash can in the neutral
                      action color, not a red ✕. An ✕ means "close this"
                      everywhere else here (it is what cancels the rename box in
                      that same table), so it read as dismissing the row rather
                      than deleting it. */}
                  {isAdded && (
                    <button
                      type="button"
                      className="btn btn-link btn-sm"
                      style={{ padding: '0 4px', color: '#766d66' }}
                      title={`Remove ${product.name} from the list`}
                      onClick={() => onRemove(product.name)}
                    >
                      <FontAwesomeIcon icon="trash-alt" />
                    </button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ClientListTable({
  products,
}: {
  products: ClientListProduct[];
}): JSX.Element {
  return (
    // Scrolls inside its own box, so a narrow window never makes the report
    // scroll sideways.
    <div style={{ overflowX: 'auto' }}>
      <table style={TABLE_STYLE}>
        {COLGROUP}
        <thead>
          <tr>{HEADER_CELLS}</tr>
        </thead>
        <tbody>
          {products.map((product, i) =>
            productLine(product, i === products.length - 1),
          )}
        </tbody>
      </table>
    </div>
  );
}

// The card's own explanation.
//
// It has one because this card sits OUTSIDE the Holobiome section, and the
// column that fills it sits inside: a practitioner who scrolls straight here,
// or who prints the report and wonders where the list came from, has no icon to
// click otherwise. Same shape as the Holobiome popover (wide rather than tall,
// capped in vw, scrolling at 70vh, "right-start" so it grows downward off the
// icon) so the two read as one kind of object.
//
// PRACTITIONER-FACING, and only on screen: the PDF builds its own card, which
// is why nothing here needs hiding from the client's copy.
function HelpPopover(): JSX.Element {
  return (
    <OverlayTrigger
      placement="right-start"
      trigger="click"
      rootClose
      overlay={
        <Popover
          id="recommended-supplements-tooltip"
          style={{ maxWidth: 'min(760px, 92vw)', width: 'max-content' }}
        >
          <Popover.Content style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div style={{ textAlign: 'left' }}>
              <strong>The list the client gets.</strong> Product, description
              and dose, with no patterns, systems or months on it. The reasoning
              stays in the Holobiome Roadmap section above, where it belongs to
              the practitioner; this is what the client is meant to act on.
              <br /><br />
              <strong>What is on it</strong> is whatever is ticked in the{' '}
              <strong>Client List</strong> column of the By System cards above.
              The <strong>Top Priority</strong> products start ticked and
              nothing else does, so the list begins at what to start with and is
              added to rather than cut down. Ticking follows the product rather
              than the row, so a product two systems recommend has one box and
              appears once here.
              <br /><br />
              <strong>Adding a product</strong> is what <strong>Edit</strong>{' '}
              is for: pick one out of the catalog, or type in anything at all
              (name, description, dose, and an optional link) for a brand
              LabSmarts does not carry. With no brand checked in Supplement
              Settings there is no catalog to pick from and nothing arrives here
              from the analysis, so this card becomes the list typed by hand.
              Unchecking a brand only hides its products; checking it back on
              puts them back where they were.
              <br /><br />
              <strong>Reset</strong> undoes the wording on one row, not the row
              itself: description, dose and link go back to what the catalog
              says, or to empty for a product typed in by hand, and the product
              stays on the list. It only appears on a row that has been changed.
              Taking a product off is the trash can beside an added row, or the{' '}
              <strong>Client List</strong> tick in the By System cards for
              everything else.
              <br /><br />
              <strong>The order</strong> is the Top Priority shortlist first, in
              its own order, then whatever else is ticked in the By System
              cards&apos; order: system by system, most concerning system first,
              and a product two systems asked for filed under the more
              concerning one.
              <br /><br />
              <strong>The dose</strong> is the standard protocol serving for
              that product&apos;s form. This card is the only place it is
              stated, which is why the Holobiome section above has no dosing
              table of its own. Not every client will be on every product, and
              it is still adjusted per client as clinically indicated.
              <br /><br />
              <strong>It saves itself.</strong> The list belongs to this test,
              not to the export, so it is still here when the test is reopened.
              Ticks are saved a moment after the last one, and the header says
              whether that has happened yet.
              <br /><br />
              <strong>Include in PDF</strong> is this card&apos;s own checkbox.
              Untick the three Holobiome boxes above and leave this one on to
              send a client the list alone. An empty list prints nothing at all.
              Because the dose is stated only here, unticking this card leaves
              the export with no servings on it.
            </div>
          </Popover.Content>
        </Popover>
      }
    >
      <FontAwesomeIcon
        icon="info-circle"
        style={{ color: '#fff', cursor: 'pointer' }}
      />
    </OverlayTrigger>
  );
}

type Props = {
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
  excluded: Set<string>;
  includeInPdf?: boolean;
  onIncludeInPdfToggle?: () => void;
  // Shown beside the title while a tick is on its way to the server, so a
  // practitioner who closes the tab knows whether their curation landed.
  saveState?: 'saving' | 'saved' | 'error';
  /** This test's practitioner edits: added products, and overridden text. */
  edits?: SupplementEdit[];
  /** Held by the analysis screen, which owns the debounce that saves them. */
  onEditsChange?: (edits: SupplementEdit[]) => void;
  /**
   * Put a product the practitioner had unticked back on the list, instead of
   * adding a second row for the same bottle. Wired to the same toggle the By
   * System checkbox column uses.
   */
  onIncludeExcluded?: (productName: string) => void;
  /** Report sections worst-first, so the picker opens where the concern is. */
  severityOrder?: Section[];
};

export const RecommendedSupplementsPanel: React.FC<Props> = ({
  bloodworkAnalysis,
  furtherConsiderations,
  enabledSupplementBrands,
  excluded,
  includeInPdf,
  onIncludeInPdfToggle,
  saveState,
  edits,
  onEditsChange,
  onIncludeExcluded,
  severityOrder,
}) => {
  const [editing, setEditing] = React.useState(false);
  // Every brand unchecked in Supplement Settings. The card stays — it is the
  // practitioner's list, not a brand's — but nothing can arrive on it from the
  // analysis, so what it offers instead is a page they type themselves.
  //
  // An empty ARRAY is that state. `undefined` is a caller that isn't filtering
  // by brand at all (the tests, and any path with no profile to hand), which is
  // the opposite thing and must not read as "no brands".
  const noBrandsEnabled =
    Array.isArray(enabledSupplementBrands) && enabledSupplementBrands.length === 0;
  // Collected ONCE, then filtered here.
  //
  // Nothing recommended at all is a different thing from everything unticked —
  // the first has no card, the second keeps one so the practitioner can see
  // where their list went and tick something back on — and answering that used
  // to mean a second full collection (the whole considerations walk) on every
  // render, which with the table above made three per checkbox tick. Filtering
  // an unfiltered collection answers both questions off one walk, and keeps
  // "recommended" meaning what it says rather than inferring it from the size
  // of the exclusion set (which can hold names this test no longer recommends).
  const allRecommended = collectClientListProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
    new Set<string>(),
    edits,
  );
  const products = allRecommended.filter(
    product => !excluded.has(normalizeSupplementName(product.name)),
  );

  // The card now stands even when the analysis pointed to nothing, because the
  // Edit button is the way to add a product and it has to be reachable. It
  // still PRINTS nothing until it has rows (RecommendedSupplementsPdf returns
  // null on an empty list), so a client never receives an empty card.
  const canEdit = !!onEditsChange;
  if (allRecommended.length === 0 && !canEdit) return null;

  // Only while editing: this is the picker's list, and nothing else on the card
  // needs the catalog walked.
  const catalog =
    editing && furtherConsiderations
      ? furtherConsiderations.supplementCatalog(enabledSupplementBrands)
      : [];
  const onList = new Set(
    products.map(product => normalizeSupplementName(product.name)),
  );
  const editsByName = indexSupplementEdits(edits);

  // Only built while editing, and the same index collectClientListProducts uses:
  // a row is the practitioner's own when NOTHING in the catalog answers to its
  // tag or its name. Matching on the name too is what stops a hand-typed
  // "EnzyGenic" being treated as their own product — addedProduct resolves that
  // row from the catalog, so its name, description and link are the catalog's.
  const editCatalog = editing ? catalogIndex(furtherConsiderations) : undefined;

  // Only built while editing — see clientListBaseTexts.
  const baseTexts = editing
    ? clientListBaseTexts(
        bloodworkAnalysis,
        furtherConsiderations,
        enabledSupplementBrands,
        excluded,
        edits || [],
      )
    : new Map<string, { description: string; dose: string; link?: string }>();

  const rows: ClientListRowEdit[] = products.map(product => {
    const key = normalizeSupplementName(product.name);
    const base = baseTexts.get(key) || {
      description: product.description,
      dose: product.dose,
      link: product.link,
    };
    const edit = editsByName.get(key);
    return {
      product,
      base,
      isAdded: edit?.origin === 'added',
      isOwnProduct:
        edit?.origin === 'added' &&
        !!editCatalog &&
        !catalogRowFor(edit, editCatalog),
      isEdited:
        product.description !== base.description ||
        product.dose !== base.dose ||
        (product.link || '') !== (base.link || ''),
    };
  });

  // Typing into a row writes an override, EXCEPT when what was typed is what
  // the catalog already said — retyping the catalog wording by hand (or
  // clearing a box back to it) leaves no override behind, so Reset disappears
  // and the record stays clean.
  const changeField = (
    name: string,
    field: 'description' | 'dose' | 'link',
    value: string,
  ) => {
    if (!onEditsChange) return;
    const key = normalizeSupplementName(name);
    const base = baseTexts.get(key);
    const existing = editsByName.get(key);
    const isAdded = existing?.origin === 'added';
    // An emptied LINK is stored as "not overridden" rather than as an empty
    // string, so clearing the box on a catalog product goes back to that
    // product's own page instead of leaving a blank override on the record.
    // Description and dose already reach the same end through
    // applySupplementOverride, and are left exactly as they were.
    const cleared = field === 'link' && !value.trim();
    const next = new SupplementEdit({
      ...(existing || {}),
      name: existing?.name || name,
      origin: isAdded ? 'added' : 'override',
      [field]: cleared || (base && value === base[field]) ? undefined : value,
    });
    onEditsChange(
      isMeaningfulSupplementEdit(next)
        ? withSupplementEdit(edits, next)
        : withoutSupplementEdit(edits, name),
    );
  };

  // Renaming a hand-typed row. Refused rather than merged when the new name is
  // one already on the list: two rows for one bottle is the thing this card
  // dedupes everywhere else, and a rename that silently swallowed the row it
  // collided with would take a product off the client's list.
  const renameProduct = (name: string, value: string) => {
    if (!onEditsChange) return;
    const key = normalizeSupplementName(value);
    if (!key || key === normalizeSupplementName(name)) return;
    if (onList.has(key) || excluded.has(key)) return;
    onEditsChange(renameSupplementEdit(edits, name, value));
  };

  const addProduct = (edit: SupplementEdit) => {
    if (!onEditsChange) return;
    const key = normalizeSupplementName(edit.name);
    // Already recommended, just unticked: put it back rather than making a
    // second row for the same bottle.
    if (excluded.has(key) && onIncludeExcluded) {
      onIncludeExcluded(edit.name);
      return;
    }
    if (onList.has(key)) return;
    onEditsChange(withSupplementEdit(edits, edit));
  };

  return (
    <Card id="recommended-supplements">
      <Card.Header
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span>
          Recommended Supplements&nbsp;&nbsp;
          <HelpPopover />
        </span>
        {onIncludeInPdfToggle && (
          <label
            htmlFor="include-in-pdf-recommended-supplements"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              margin: 0,
              fontWeight: 'normal',
              cursor: 'pointer',
              color: '#fff',
              whiteSpace: 'nowrap',
            }}
            title="Include this section in the Analysis PDF"
          >
            <span style={{ marginRight: '6px' }}>Include in PDF</span>
            <input
              id="include-in-pdf-recommended-supplements"
              type="checkbox"
              className="lsm-styled-checkbox"
              checked={!!includeInPdf}
              onChange={onIncludeInPdfToggle}
            />
          </label>
        )}
      </Card.Header>
      <Card.Body>
        <div
          style={{
            fontSize: '11.5px',
            color: '#6c757d',
            lineHeight: 1.45,
            marginBottom: '8px',
          }}
        >
          {LEAD_IN}
        </div>
        {products.length > 0 ? (
          editing ? (
            <EditableClientList
              rows={rows}
              onFieldChange={changeField}
              onRename={renameProduct}
              onReset={name => onEditsChange && onEditsChange(resetSupplementEdit(edits, name))}
              onRemove={name => onEditsChange && onEditsChange(withoutSupplementEdit(edits, name))}
            />
          ) : (
            <ClientListTable products={products} />
          )
        ) : (
          // Practitioner-facing, and only reachable on screen: an empty list
          // prints nothing at all.
          <div style={{ fontSize: '12px', color: '#6c757d' }}>
            {allRecommended.length === 0 ? (
              // Two different silences, and saying so is the difference between
              // "this client's blood work was quiet" and "nothing could have
              // been recommended, because no brand is switched on". The second
              // one is a settings answer, so it names the setting.
              noBrandsEnabled ? (
                <>
                  No supplement brand is switched on in{' '}
                  <strong>Report Settings &rarr; Supplement Settings</strong>, so
                  nothing was recommended from this test.{' '}
                  {canEdit && (
                    <>
                      Use <strong>Edit</strong> to build the list yourself:
                      product, description and dose, from any brand.
                    </>
                  )}
                </>
              ) : (
                <>
                  This test did not point to any supplements.{' '}
                  {canEdit && <>Use <strong>Edit</strong> to add them yourself.</>}
                </>
              )
            ) : (
              <>
                Nothing is on the client&apos;s list. Tick{' '}
                <strong>Client List</strong> beside a product in the By System
                view to put it back.
              </>
            )}
          </div>
        )}
        {/* The edit affordance, under the content and shaped like the one on
            every Notes card — a Bootstrap button that says what it does.
            It lived in the blue header as an icon first, which put it where
            nobody looks for a control and made it a third way of saying "edit"
            in a report that already has one. */}
        {canEdit && (
          <div
            style={{
              marginTop: 10,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
            }}
          >
            <Button
              type="button"
              onClick={() => setEditing(!editing)}
              title={
                editing
                  ? 'Back to the list as the client will read it'
                  : 'Add a supplement, or change what a row says'
              }
            >
              {editing ? 'Done' : 'Edit'}
            </Button>
            {editing && (
              <SupplementPicker
                catalog={catalog}
                severityOrder={severityOrder || []}
                inUse={onList}
                onAdd={addProduct}
              />
            )}
            {/* Beside the button that caused it, not up in the title. This card
                is the only thing in the report that saves itself, so the state
                of that write has to be visible somewhere — but only while it
                means something (see the parent, which clears "Saved" a few
                seconds after it lands). */}
            {saveState && (
              <span
                style={{ fontSize: '12px', color: saveState === 'error' ? '#c0392b' : '#6c757d' }}
                aria-live="polite"
              >
                {saveState === 'saving' && 'Saving…'}
                {saveState === 'saved' && 'Changes saved'}
                {saveState === 'error' && 'Not saved — check your connection'}
              </span>
            )}
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

// ───────────────────────── PDF ─────────────────────────
//
// One flat list, pre-paginated the way every long block in this report is (see
// pdfSupplementsPagination.ts for why html2pdf's own keep-together pass cannot
// be trusted this deep into the document). No checkbox column: the decision was
// made on screen, and what prints is its result.

const PDF_PAGE_CAPACITY = 900;
const PDF_CARD_OVERHEAD = 100;
// A line carries a wrapping description, so it is sized for two lines the way
// the by-system rows are.
const PDF_ITEM_HEIGHT = 46;
// The lead-in above the table, plus the column headings.
const PDF_INTRO_HEIGHT = 60;
const PDF_TITLE = 'Recommended Supplements';

function pdfPages(products: ClientListProduct[]): SupplementsBlock<ClientListProduct>[][] {
  return packSupplementBlocks<ClientListProduct>({
    blocks: [
      {
        key: 'client-list',
        brandLabel: null,
        label: null,
        items: products,
        isContinuation: false,
      },
    ],
    pageCapacity: PDF_PAGE_CAPACITY,
    cardOverhead: PDF_CARD_OVERHEAD + PDF_INTRO_HEIGHT,
    brandHeadingHeight: 0,
    patternHeadingHeight: 0,
    itemHeight: PDF_ITEM_HEIGHT,
  });
}

// How tall the LAST printed page is. The container uses it as a "is there
// anything to print" gate rather than for placement (the Disclaimer decides its
// own page from real geometry now), so the number itself only has to be right
// about being zero — but it is computed properly so it stays usable if the
// placement math ever wants it again.
export function estimatedRecommendedSupplementsPdfHeight(
  bloodworkAnalysis: BloodworkAnalysis | undefined,
  furtherConsiderations: FurtherConsiderations | undefined,
  enabledSupplementBrands: string[] | undefined,
  excluded: Set<string>,
  edits?: SupplementEdit[],
): number {
  const products = collectClientListProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
    excluded,
    edits,
  );
  if (products.length === 0) return 0;
  const pages = pdfPages(products);
  const last = pages[pages.length - 1];
  const count = last.reduce((n, block) => n + block.items.length, 0);
  return PDF_CARD_OVERHEAD + PDF_INTRO_HEIGHT + count * PDF_ITEM_HEIGHT;
}

export const RecommendedSupplementsPdf: React.FC<{
  bloodworkAnalysis: BloodworkAnalysis;
  furtherConsiderations: FurtherConsiderations;
  enabledSupplementBrands?: string[];
  excluded: Set<string>;
  edits?: SupplementEdit[];
}> = ({
  bloodworkAnalysis,
  furtherConsiderations,
  enabledSupplementBrands,
  excluded,
  edits,
}) => {
  const products = collectClientListProducts(
    bloodworkAnalysis,
    furtherConsiderations,
    enabledSupplementBrands,
    excluded,
    edits,
  );
  if (products.length === 0) return null;
  const pages = pdfPages(products);

  return (
    <>
      {pages.map((blocks, pageIdx) => {
        const items = blocks.flatMap(block => block.items);
        return (
          <React.Fragment key={`client-list-page-${pageIdx}`}>
            {pageIdx > 0 && (
              <>
                <div className="html2pdf__page-break" />
                {/* Same top buffer the container puts before the first page: a
                    forced break lands a hair above the true page edge, and
                    without the spacer the card header gets clipped. */}
                <div style={{ height: 28 }} aria-hidden />
              </>
            )}
            <Card className="disclaimer-panel mb-5">
              <Card.Header className="py-1">
                {pageIdx === 0 ? PDF_TITLE : `${PDF_TITLE} (cont.)`}
              </Card.Header>
              <Card.Body
                className="disclaimer-body note-card-body"
                style={{ paddingBottom: 12 }}
              >
                {pageIdx === 0 && (
                  <div
                    style={{
                      fontSize: '11.5px',
                      color: '#6c757d',
                      lineHeight: 1.45,
                      marginBottom: '8px',
                    }}
                  >
                    {LEAD_IN}
                  </div>
                )}
                <ClientListTable products={items} />
              </Card.Body>
            </Card>
          </React.Fragment>
        );
      })}
    </>
  );
};
